import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Highlight from "@/components/base/Highlight";
import EmptyState from "@/components/base/EmptyState";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { CATEGORY_KEYS, decadeOptions, getPopularFilms, searchFilms } from "@/lib/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";

const hotKeywords = ["家庭伦理", "悬疑", "韩国", "短片", "雨夜", "女性视角", "照护", "记忆"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [category, setCategory] = useState("all");
  const [decade, setDecade] = useState("all");

  usePageMeta(
    query ? `搜索「${query}」_日韩伦理片站内检索` : "站内搜索_日韩伦理片影视资料检索",
    query
      ? `在日韩伦理片资料库中检索「${query}」的结果，可按类型与年代进一步筛选，包含作品简介、导演与主演信息。`
      : "按片名、导演、主演、类型或主题标签检索日韩伦理影视作品，支持模糊匹配与类型、年代筛选。",
  );

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = query ? searchFilms(query, { category, decade }) : [];
  const popular = getPopularFilms(4);

  const submit = (value: string) => {
    const next = value.trim();
    if (next) {
      setSearchParams({ q: next });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-shell px-4 pb-9 pt-6 md:px-8 md:pb-11 md:pt-8">
          <Breadcrumbs items={[{ label: "搜索" }]} />
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-700">
            Site Search
          </p>
          <h1 className="mt-2 font-heading text-[32px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[40px]">
            站内搜索
          </h1>
          <p className="mt-2.5 max-w-2xl text-[13px] leading-relaxed text-foreground-600 md:text-sm">
            支持片名、外文名、导演、主演、类型与主题标签的模糊匹配。输入关键词后按回车即可查看结果。
          </p>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(input);
            }}
            className="mt-5 flex flex-col gap-2.5 sm:flex-row"
          >
            <div className="flex h-12 flex-1 items-center rounded-full border border-background-300 bg-background-50 pl-4 pr-1 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line text-base text-secondary-500" />
              <input
                type="search"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="例如：雨停之前 / 韩素理 / 家庭伦理"
                aria-label="输入搜索关键词"
                className="w-full bg-transparent px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="flex h-10 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-800 px-4 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                搜索
                <i className="ri-arrow-right-line text-xs" />
              </button>
            </div>
          </form>

          <div className="mt-3.5 flex flex-wrap items-center gap-2">
            <span className="font-label text-[11px] text-foreground-400">热门关键词</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => submit(word)}
                className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 pt-8 md:px-8 md:pt-10">
        {query ? (
          <>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex flex-col gap-4">
                <p className="text-[13px] text-foreground-700">
                  关键词 <span className="font-semibold text-primary-800">“{query}”</span> 共找到{" "}
                  <span className="font-label font-bold text-accent-700">{results.length}</span> 条结果
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="mr-1 font-label text-[10px] uppercase tracking-[0.16em] text-secondary-500">类型</span>
                    <button
                      type="button"
                      onClick={() => setCategory("all")}
                      className={`cursor-pointer rounded-full px-3 py-1.5 text-[11px] transition-colors ${
                        category === "all"
                          ? "bg-primary-800 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      全部
                    </button>
                    {CATEGORY_KEYS.map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setCategory(key)}
                        className={`cursor-pointer rounded-full px-3 py-1.5 text-[11px] transition-colors ${
                          category === key
                            ? "bg-primary-800 text-background-50"
                            : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                        }`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
                    <span className="mr-1 font-label text-[10px] uppercase tracking-[0.16em] text-secondary-500">年代</span>
                    {decadeOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setDecade(option.key)}
                        className={`cursor-pointer rounded-full px-3 py-1.5 text-[11px] transition-colors ${
                          decade === option.key
                            ? "bg-secondary-800 text-background-50"
                            : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {results.length > 0 ? (
              <div className="mt-6 flex flex-col gap-4">
                {results.map((film) => (
                  <article
                    key={film.id}
                    className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-3.5 transition-colors hover:border-primary-300 sm:flex-row sm:p-4"
                  >
                    <Link
                      to={`/detail/${film.id}`}
                      className="relative block h-[168px] w-full shrink-0 cursor-pointer overflow-hidden rounded-md bg-secondary-100 sm:h-[150px] sm:w-[100px]"
                    >
                      <img
                        src={film.poster}
                        alt={`${film.title} 作品封面`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    </Link>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] text-secondary-900">
                          {film.category}
                        </span>
                        <span className="font-label text-[10px] text-foreground-400">{film.region} · {film.year}</span>
                        <span className="flex items-center gap-0.5 font-label text-[10px] font-semibold text-accent-700">
                          <i className="ri-star-fill text-[10px]" />
                          {film.score.toFixed(1)}
                        </span>
                      </div>
                      <Link
                        to={`/detail/${film.id}`}
                        className="mt-1.5 cursor-pointer font-heading text-[17px] font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-700"
                      >
                        <Highlight text={film.title} keyword={query} />
                        <span className="ml-2 font-label text-[10px] font-normal uppercase tracking-wider text-foreground-400">
                          <Highlight text={film.titleEn} keyword={query} />
                        </span>
                      </Link>
                      <p className="mt-1.5 text-[12px] text-foreground-600">
                        <Highlight text={`${film.director} 执导 · ${film.cast.join("、")}`} keyword={query} />
                      </p>
                      <p className="mt-2 line-clamp-2 text-[12px] leading-relaxed text-foreground-500">
                        <Highlight text={film.summary} keyword={query} />
                      </p>
                      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2.5">
                        {film.tags.slice(0, 4).map((tag) => (
                          <span key={tag} className="rounded-full border border-background-200 px-2 py-0.5 text-[10px] text-foreground-500">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-6">
                <EmptyState
                  icon="ri-search-line"
                  title={`没有找到与“${query}”匹配的作品`}
                  description="试试更换关键词，或者先去掉类型与年代的筛选条件。片名、导演、主演、类型与标签都可以被搜索到。"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setCategory("all");
                      setDecade("all");
                    }}
                    className="flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-800 px-4 py-2 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-restart-line text-xs" />
                    清除筛选条件
                  </button>
                  <Link
                    to="/category/%E7%94%B5%E5%BD%B1"
                    className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-[12px] text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    浏览电影分类
                  </Link>
                </EmptyState>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-100/60 px-5 py-10 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-secondary-600">
              <i className="ri-search-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">先输入一个关键词</h2>
            <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-foreground-600">
              可以试试片名、导演、主演，或者「家庭伦理」「悬疑」这样的类型词。下面是目前站内热度最高的几部作品。
            </p>
          </div>
        )}
      </section>

      <section className="mx-auto max-w-shell px-4 pt-12 md:px-8 md:pt-14">
        <SectionHeading
          kicker="Popular Now"
          title={query && results.length === 0 ? "也许你会喜欢这些" : "热门推荐"}
          description="按站内热度排序的作品，适合作为下一步的阅读入口。"
          actionLabel="全部专题"
          actionTo="/specials"
        />
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
          {popular.map((film) => (
            <FilmCard key={film.id} film={film} showSummary={false} />
          ))}
        </div>
      </section>
    </div>
  );
}